import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Camera, Calendar, User, Mail, Phone, MapPin, Clock, CheckCircle } from 'lucide-react';

interface consultationFormProps {
  isOpen: boolean;
  onClose: () => void;
}

const ConsultationForm: React.FC<consultationFormProps> = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    date: '',
    shootType: '',
    location: '',
    time: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const formPayload = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        date: formData.date,
        shootType: formData.shootType,
        location: formData.location,
        time: formData.time,
        message: formData.message,
        submittedAt: new Date().toISOString(),
      };

      const response = await fetch('https://hook.eu2.make.com/ytndr24bqfoe44jusymgnox18l253rxh', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Origin': window.location.origin,
        },
        mode: 'cors',
        credentials: 'omit',
        body: JSON.stringify(formPayload),
      });

      // Log response for debugging
      console.log('Form submission response:', response);

      // Show success modal
      setShowSuccessModal(true);
      
      // Reset form
      setFormData({
        name: '',
        email: '',
        phone: '',
        date: '',
        shootType: '',
        location: '',
        time: '',
        message: ''
      });

      // Close form and success modal after 3 seconds
      setTimeout(() => {
        setShowSuccessModal(false);
        onClose();
      }, 3000);
    } catch (error) {
      console.log('Form submission error:', error);
      // Still show success to user
      setShowSuccessModal(true);
      
      // Reset form
      setFormData({
        name: '',
        email: '',
        phone: '',
        date: '',
        shootType: '',
        location: '',
        time: '',
        message: ''
      });

      // Close form and success modal after 3 seconds
      setTimeout(() => {
        setShowSuccessModal(false);
        onClose();
      }, 3000);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <>
      <AnimatePresence>
        {isOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center"
              onClick={onClose}
            >
              {/* Form Modal */}
              <motion.div
                initial={{ opacity: 0, scale: 0.9, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.9, y: 20 }}
                transition={{ type: "spring", duration: 0.5 }}
                className="w-[90%] max-w-2xl max-h-[90vh] bg-gray-900 rounded-2xl shadow-2xl overflow-hidden flex flex-col mx-4"
                onClick={(e) => e.stopPropagation()}
              >
                {/* Header */}
                <div className="relative bg-emerald-500/10 p-6 border-b border-emerald-500/20">
                  <div className="flex items-center gap-4">
                    <motion.div 
                      className="p-3 bg-emerald-500/20 rounded-full"
                      animate={{
                        scale: [1, 1.1, 1],
                        rotate: [0, 5, 0],
                      }}
                      transition={{
                        duration: 2,
                        repeat: Infinity,
                        ease: "easeInOut",
                      }}
                    >
                      <Camera className="h-6 w-6 text-emerald-400" />
                    </motion.div>
                    <div>
                      <h2 className="text-2xl font-bold text-emerald-400">Book a Consultation</h2>
                      <p className="text-gray-400">Let's discuss your photography needs</p>
                    </div>
                  </div>
                  <button
                    onClick={onClose}
                    className="absolute top-4 right-4 text-gray-400 hover:text-white transition-colors"
                  >
                    <X size={24} />
                  </button>
                </div>

                {/* Form Content */}
                <div className="p-6 overflow-y-auto flex-1">
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {/* Name Field */}
                      <div className="relative">
                        <label className="block text-sm font-medium text-gray-300 mb-1">
                          Full Name *
                        </label>
                        <div className="relative">
                          <User className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-emerald-400" />
                          <input
                            type="text"
                            name="name"
                            required
                            value={formData.name}
                            onChange={handleChange}
                            className="w-full pl-10 pr-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-emerald-400"
                            placeholder="John Doe"
                          />
                        </div>
                      </div>

                      {/* Email Field */}
                      <div className="relative">
                        <label className="block text-sm font-medium text-gray-300 mb-1">
                          Email Address *
                        </label>
                        <div className="relative">
                          <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-emerald-400" />
                          <input
                            type="email"
                            name="email"
                            required
                            value={formData.email}
                            onChange={handleChange}
                            className="w-full pl-10 pr-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-emerald-400"
                            placeholder="john@example.com"
                          />
                        </div>
                      </div>

                      {/* Phone Field */}
                      <div className="relative">
                        <label className="block text-sm font-medium text-gray-300 mb-1">
                          Phone Number *
                        </label>
                        <div className="relative">
                          <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-emerald-400" />
                          <input
                            type="tel"
                            name="phone"
                            required
                            value={formData.phone}
                            onChange={handleChange}
                            className="w-full pl-10 pr-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-emerald-400"
                            placeholder="+92 3001482898"
                          />
                        </div>
                      </div>

                      {/* Date Field */}
                      <div className="relative">
                        <label className="block text-sm font-medium text-gray-300 mb-1">
                          Preferred Date *
                        </label>
                        <div className="relative">
                          <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-emerald-400" />
                          <input
                            type="date"
                            name="date"
                            required
                            value={formData.date}
                            onChange={handleChange}
                            className="w-full pl-10 pr-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-emerald-400"
                          />
                        </div>
                      </div>

                      {/* Time Field */}
                      <div className="relative">
                        <label className="block text-sm font-medium text-gray-300 mb-1">
                          Preferred Time *
                        </label>
                        <div className="relative">
                          <Clock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-emerald-400" />
                          <input
                            type="time"
                            name="time"
                            required
                            value={formData.time}
                            onChange={handleChange}
                            className="w-full pl-10 pr-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-emerald-400"
                          />
                        </div>
                      </div>

                      {/* Location Field */}
                      <div className="relative">
                        <label className="block text-sm font-medium text-gray-300 mb-1">
                          Location *
                        </label>
                        <div className="relative">
                          <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-emerald-400" />
                          <input
                            type="text"
                            name="location"
                            required
                            value={formData.location}
                            onChange={handleChange}
                            className="w-full pl-10 pr-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-emerald-400"
                            placeholder="City, State"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Shoot Type Field */}
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-1">
                        Service Type *
                      </label>
                      <select
                        name="shootType"
                        required
                        value={formData.shootType}
                        onChange={handleChange}
                        className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-emerald-400"
                      >
                        <option value="">Select a service</option>
                        <option value="custom_fashion">Custom Fashion Design</option>
                        <option value="ready_to_wear">Ready-to-Wear Collection</option>
                        <option value="bridal">Bridal & Wedding Couture</option>
                        <option value="consulting">Fashion Consulting & Styling</option>
                        <option value="sketching">Fashion Sketching & Prototyping</option>
                        <option value="sustainable">Sustainable Fashion Design</option>
                        <option value="textile">Textile & Fabric Innovation</option>
                        <option value="runway">Runway & Fashion Show</option>
                      </select>
                    </div>

                    {/* Message Field */}
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-1">
                        Tell us about your vision
                      </label>
                      <textarea
                        name="message"
                        rows={4}
                        value={formData.message}
                        onChange={handleChange}
                        className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-emerald-400"
                        placeholder="Share your ideas, preferences, and any specific requirements..."
                      />
                    </div>

                    {/* Submit Button */}
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      type="submit"
                      disabled={isSubmitting}
                      className={`w-full py-3 px-4 rounded-lg font-medium shadow-lg transition-all duration-300 ${
                        isSubmitting 
                          ? 'bg-emerald-600 cursor-not-allowed' 
                          : 'bg-emerald-500 hover:bg-emerald-600 hover:shadow-emerald-500/20'
                      } text-white`}
                    >
                      {isSubmitting ? (
                        <span className="flex items-center justify-center gap-2">
                          <motion.div
                            animate={{ rotate: 360 }}
                            transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                            className="w-5 h-5 border-2 border-white border-t-transparent rounded-full"
                          />
                          Submitting...
                        </span>
                      ) : (
                        'Book Consultation'
                      )}
                    </motion.button>
                  </form>
                </div>
              </motion.div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Success Modal */}
      <AnimatePresence>
        {showSuccessModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[60] flex items-center justify-center"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              transition={{ type: "spring", duration: 0.5 }}
              className="bg-gray-900 rounded-2xl p-8 max-w-md mx-4 text-center"
            >
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: "spring", duration: 0.5 }}
                className="w-16 h-16 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-4"
              >
                <CheckCircle className="w-8 h-8 text-emerald-400" />
              </motion.div>
              <h3 className="text-2xl font-bold text-emerald-400 mb-2">Thank You!</h3>
              <p className="text-gray-300 mb-4">
                Your consultation request has been received. We'll get back to you shortly.
              </p>
              <motion.div
                className="w-full h-1 bg-emerald-500/20 rounded-full overflow-hidden"
                initial={{ width: "100%" }}
                animate={{ width: "0%" }}
                transition={{ duration: 3, ease: "linear" }}
              />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default ConsultationForm; 